import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpResponse,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, throwError, timer } from 'rxjs';
import { catchError, map, retryWhen, mergeMap, delayWhen, take } from 'rxjs/operators';
import { APP_ROUTES } from 'src/app/core/config/routes';
import { APP_MOCKS } from 'src/app/core/endpoints/app.endpoints';
import { AuthService } from 'src/app/features/auth/services/auth.service';
import { SharedService } from '../services/shared.service';
import { NzMessageService } from 'ng-zorro-antd/message';

@Injectable()
export class HttpMockInterceptor implements HttpInterceptor {
  isLoading = false;
  counter: number = 0;
  private readonly MAX_RETRIES = 3; // Maximum number of retries
  private readonly RETRY_DELAY = 1000; // Delay between retries in ms

  constructor(
    private sharedService: SharedService,
    private toastrService: ToastrService,
    private spinnerService: NgxSpinnerService,
    private authService: AuthService,
    private router: Router,
    private message: NzMessageService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    if (this.sharedService.isMock) {
      if (
        req.method !== 'GET' &&
        !req.url.includes('signin') &&
        !req.url.includes('otp')
      ) {
        // Your mock logic here
      }
      const mock = APP_MOCKS[req.url];
      if (mock) {
        return of(
          new HttpResponse({
            status: 200,
            body: { data: mock }
          })
        );
      }
    } else {
      this.counter++;
     
      if (req.method !== 'GET') this.spinnerService.show();

      return next.handle(req).pipe(
        // Add retry logic for GET requests
        this.shouldRetry(req.method),
        map((event: HttpEvent<any>) => {
          if (event instanceof HttpResponse) {
            this.counter--;
            if (!this.counter) {
              this.spinnerService.hide();
            }
            if (this.counter < 0) this.counter = 0;
          }
          return event;
        }),
        catchError((error) => {
          if (error instanceof HttpErrorResponse) {
            this.counter--;
            if (!this.counter) {
              this.spinnerService.hide();
              
              const arabic = /[\u0600-\u06FF]/;
              const errorMessage = error?.error?.message || '';
              const isArabic = arabic.test(errorMessage);

              switch (error.status) {
                case 406:
                case 400:
                case 404:
                case 409:
                  if (isArabic) {
                    this.message.error(errorMessage);
                  } else {
                    this.message.error('الرجاء التثبت');
                  }
                  break;
                
                case 401:
                case 403:
                  if (isArabic) {
                    this.message.error(errorMessage);
                  } else {
                    localStorage.clear();
                    this.sharedService.connectedUserInfo = null;
                    this.router.navigate([APP_ROUTES.AUTH.LOGIN]).then(
                      () => window.location.reload()
                    );
                  }
                  break;
                
                default:
                  this.message.error('عذرا لقد حصل خطأ');
              }
            }

            return throwError(error);
          }
          return throwError(error);
        })
      );
    }
  }

  // Custom retry operator
  private shouldRetry(method: string): any {
    return (source: Observable<any>) => {
      if (method === 'GET') {
        return source.pipe(
          retryWhen(errors => errors.pipe(
            mergeMap((error, i) => {
              // Don't retry for 401/403 or if we've reached the max retries
              if (error.status === 401 || error.status === 403 || i >= this.MAX_RETRIES - 1) {
                return throwError(error);
              }
              // Retry for timeout (status 0) or server errors (5xx)
              if (error.status === 0 || (error.status >= 500 && error.status < 600)) {
                return timer(this.RETRY_DELAY);
              }
              return throwError(error);
            }),
            take(this.MAX_RETRIES)
          ))
        );
      }
      return source;
    };
  }
}